import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, AlertCircle, X, ChevronDown,
  FileCheck, Eye
} from 'lucide-react';
import axios from '../axiosInstance';

/* ───────────── helpers ───────────── */
const formatCurrency = (val) => {
  if (val === null || val === undefined || val === '') return '—';
  return `₹${parseFloat(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/* ───────────── component ───────────── */
export default function SlaForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  /* master data */
  const [seasons, setSeasons] = useState([]);
  const [commodities, setCommodities] = useState([]);

  /* form state */
  const [form, setForm] = useState({
    season_id: '',
    commodity_id: '',
    bags: '',
    quantity: '',
  });

  /* preview data (from GET /sla/preview) */
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  /* general */
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  /* ── load master data ── */
  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, cRes] = await Promise.all([
          axios.get('seasons?paginate=false'),
          axios.get('commodity'),
        ]);
        setSeasons(sRes.data?.seasons || sRes.data?.data || (Array.isArray(sRes.data) ? sRes.data : []));
        setCommodities(cRes.data?.commodities || cRes.data?.data || (Array.isArray(cRes.data) ? cRes.data : []));
      } catch (err) {
        console.error('Master data load error:', err);
        setError('Failed to load seasons/commodities. Please refresh.');
      }
    };
    load();
  }, []);

  /* ── load existing SLA for edit ── */
  useEffect(() => {
    if (!isEdit) return;
    const loadSla = async () => {
      setFetchLoading(true);
      try {
        const res = await axios.get(`sla/${id}`);
        const sla = res.data?.data || res.data;
        setForm({
          season_id: sla.season_id || sla.season?.id || '',
          commodity_id: sla.commodity_id || sla.commodity?.id || '',
          bags: sla.bags ?? '',
          quantity: sla.quantity ?? '',
        });
        // Also set preview from existing data
        if (sla.msp || sla.amount) {
          setPreview({ msp: sla.msp, amount: sla.amount });
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load SLA entry');
      } finally {
        setFetchLoading(false);
      }
    };
    loadSla();
  }, [id, isEdit]);

  /* ── fetch preview when season + commodity are selected ── */
  const fetchPreview = useCallback(async (seasonId, commodityId, quantity) => {
    if (!seasonId || !commodityId) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('season_id', seasonId);
      params.append('commodity_id', commodityId);
      if (quantity) params.append('quantity', quantity);

      const res = await axios.get(`sla/preview?${params.toString()}`);
      const data = res.data?.data || res.data;
      setPreview(data);
    } catch (err) {
      console.error('Preview error:', err);
      // Don't block form if preview fails
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  /* ── auto-trigger preview ── */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (form.season_id && form.commodity_id) {
        fetchPreview(form.season_id, form.commodity_id, form.quantity);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.season_id, form.commodity_id, form.quantity, fetchPreview]);

  /* ── form handlers ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!form.season_id || !form.commodity_id || !form.bags || !form.quantity) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        season_id: form.season_id,
        commodity_id: form.commodity_id,
        bags: parseInt(form.bags, 10),
        quantity: parseFloat(form.quantity),
      };

      if (isEdit) {
        await axios.patch(`sla/${id}`, payload);
        setSuccess('SLA entry updated successfully!');
      } else {
        await axios.post('sla', payload);
        setSuccess('SLA entry created successfully!');
      }

      setTimeout(() => {
        navigate('/incidental/sla');
      }, 1200);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} SLA entry`);
    } finally {
      setLoading(false);
    }
  };

  /* ── render ── */
  if (fetchLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-blue-600">
          <Loader2 size={40} className="animate-spin" />
          <span className="font-bold text-sm">Loading SLA entry...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 text-slate-800 font-sans">
      <div className="max-w-3xl mx-auto">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/incidental/sla')}
            className="p-3 bg-white border border-slate-200 hover:border-blue-300 rounded-2xl shadow-sm transition-all"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </motion.button>
          <div>
            <h5 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Incidental Management</h5>
            <h1 className="text-3xl md:text-4xl font-black text-blue-800 tracking-tight">
              {isEdit ? 'Edit SLA Entry' : 'Create SLA Entry'}
            </h1>
          </div>
        </motion.div>

        {/* ── Success ── */}
        <AnimatePresence>
          {success && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-6 flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-2xl text-sm font-semibold">
              <FileCheck size={20} /> {success}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Error ── */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-6 flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-semibold">
              <AlertCircle size={20} /> {error}
              <button onClick={() => setError('')} className="ml-auto p-1 hover:bg-red-100 rounded-lg transition"><X size={16} /></button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Form ── */}
        <motion.form
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 md:p-8 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Season */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Season <span className="text-red-500">*</span></label>
              <div className="relative">
                <select
                  name="season_id"
                  value={form.season_id}
                  onChange={handleChange}
                  required
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer text-sm"
                >
                  <option value="">Select Season</option>
                  {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Commodity */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Commodity <span className="text-red-500">*</span></label>
              <div className="relative">
                <select
                  name="commodity_id"
                  value={form.commodity_id}
                  onChange={handleChange}
                  required
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer text-sm"
                >
                  <option value="">Select Commodity</option>
                  {commodities.map(c => <option key={c.id} value={c.id}>{c.commodityname || c.name}</option>)}
                </select>
                <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Bags */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Bags <span className="text-red-500">*</span></label>
              <input
                type="number"
                name="bags"
                value={form.bags}
                onChange={handleChange}
                required
                min="1"
                placeholder="Enter number of bags"
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
              />
            </div>

            {/* Quantity */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Quantity (Qtl) <span className="text-red-500">*</span></label>
              <input
                type="number"
                name="quantity"
                value={form.quantity}
                onChange={handleChange}
                required
                min="0.01"
                step="0.01"
                placeholder="Enter quantity in quintals"
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
              />
            </div>
          </div>

          {/* ── MSP Preview ── */}
          <AnimatePresence>
            {(preview || previewLoading) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl border border-indigo-100 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Eye size={18} className="text-indigo-600" />
                    <h3 className="text-sm font-black text-indigo-800 uppercase tracking-wider">MSP Preview</h3>
                    {previewLoading && <Loader2 size={16} className="animate-spin text-indigo-500" />}
                  </div>
                  {preview && !previewLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white/70 rounded-2xl p-4 border border-indigo-100">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">MSP (₹/Qtl)</p>
                        <p className="text-2xl font-black text-indigo-700">{formatCurrency(preview.msp)}</p>
                      </div>
                      <div className="bg-white/70 rounded-2xl p-4 border border-indigo-100">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Calculated Amount</p>
                        <p className="text-2xl font-black text-emerald-700">{formatCurrency(preview.amount)}</p>
                      </div>
                    </div>
                  ) : previewLoading ? (
                    <div className="flex items-center gap-3 text-indigo-500 py-2">
                      <Loader2 size={20} className="animate-spin" />
                      <span className="text-sm font-semibold">Fetching MSP data...</span>
                    </div>
                  ) : null}
                  <p className="text-xs text-indigo-500 mt-3">
                    * MSP and amount are auto-calculated by the backend. This is a preview only.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Actions ── */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/incidental/sla')}
              className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl font-bold text-sm transition"
            >
              Cancel
            </motion.button>
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={loading}
              className="flex items-center gap-2 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update SLA' : 'Create SLA')}
            </motion.button>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
